/**
 * Utility functions for Google Places API integration
 */

import {
  AutocompletePrediction,
  PlaceResult,
  SuggestionREST,
  AddressComponentREST,
  PlaceDetailsResponseREST,
} from './types';

// ==================== Field Mapping ====================

/**
 * Maps old JavaScript API field names to new REST API field names
 */
const FIELD_MAPPING: Record<string, string> = {
  address_components: 'addressComponents',
  formatted_address: 'formattedAddress',
  'geometry.location': 'location',
  place_id: 'id',
  name: 'displayName',
};

/**
 * Default fields to fetch if none specified
 */
const DEFAULT_FIELDS = ['id', 'displayName', 'formattedAddress', 'location', 'addressComponents'];

/**
 * Maps request fields to REST API field mask
 * Converts old API field names to new REST API field names
 *
 * @param fields - Array of field names to fetch
 * @returns Comma-separated field mask string
 */
export function mapFieldsToFieldMask(fields?: string[]): string {
  const mappedFields = fields?.map((field) => FIELD_MAPPING[field] || field) || DEFAULT_FIELDS;
  return mappedFields.join(',');
}

// ==================== Response Transformers ====================

/**
 * Transforms REST API autocomplete suggestions to standardized predictions
 *
 * @param suggestions - Array of suggestions from REST API
 * @returns Array of standardized autocomplete predictions
 */
export function transformAutocompleteSuggestions(suggestions: SuggestionREST[]): AutocompletePrediction[] {
  return suggestions
    .filter((suggestion) => suggestion.placePrediction)
    .map((suggestion) => {
      const placePrediction = suggestion.placePrediction!;
      return {
        place_id: placePrediction.placeId || placePrediction.place || '',
        description: placePrediction.text?.text || '',
        structured_formatting: {
          main_text: placePrediction.structuredFormat?.mainText?.text || placePrediction.text?.text || '',
          secondary_text: placePrediction.structuredFormat?.secondaryText?.text || '',
        },
      };
    });
}

/**
 * Transforms REST API place details response to standardized place result
 *
 * @param data - Place details response from REST API
 * @param placeId - The place ID (fallback if not in response)
 * @returns Standardized place result
 */
export function transformPlaceDetails(data: PlaceDetailsResponseREST, placeId: string): PlaceResult {
  return {
    place_id: data.id || placeId,
    name: data.displayName?.text || '',
    formatted_address: data.formattedAddress || '',
    address_components: transformAddressComponents(data.addressComponents || []),
    geometry: data.location
      ? {
          location: {
            lat: () => data.location!.latitude,
            lng: () => data.location!.longitude,
          },
        }
      : undefined,
    html_attributions: [],
  };
}

/**
 * Transforms REST API address components to standardized format
 *
 * @param components - Array of address components from REST API
 * @returns Array of standardized address components
 */
function transformAddressComponents(components: AddressComponentREST[]) {
  return components.map((component) => ({
    long_name: component.longText || '',
    short_name: component.shortText || '',
    types: component.types || [],
  }));
}

// ==================== Validators ====================

/**
 * Validates that an input string is not empty
 *
 * @param input - Input string to validate
 * @returns True if input is valid
 */
export function isValidInput(input: string): boolean {
  return Boolean(input && input.trim().length > 0);
}
